'use client';

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, MapPin, Check } from 'lucide-react';
import { QUEBEC_CITIES } from '@/lib/constants/quebec-cities';
import { cn } from '@/lib/utils';
import { Input } from '@/components/common/inputs/text/input';
import { useScopedI18n } from '@/locales/client';

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const CityAutocomplete: React.FC<CityAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Search city...',
}) => {
  const t = useScopedI18n('formQuestionnaire.q0');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter cities based on search term
  const filteredCities = useMemo(() => {
    if (!searchTerm.trim()) return QUEBEC_CITIES;
    const term = searchTerm.toLowerCase();
    return QUEBEC_CITIES.filter((city) =>
      city.toLowerCase().includes(term)
    ).slice(0, 10); // Show top 10 results
  }, [searchTerm]);

  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (city: string) => {
    onChange(city);
    setSearchTerm(city);
    setIsOpen(false);
  };

  return (
    <div className='relative w-full' ref={containerRef}>
      <div className='relative'>
        <Input
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className='h-12 w-full pr-10 text-lg focus-visible:ring-purple-600'
        />
        <div className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400'>
          <Search className='h-5 w-5' />
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (filteredCities.length > 0 || searchTerm.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className='absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900'
          >
            <div className='max-h-[300px] overflow-y-auto p-2'>
              {filteredCities.length > 0 ? (
                filteredCities.map((city) => (
                  <button
                    key={city}
                    type='button'
                    onClick={() => handleSelect(city)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-lg px-4 py-3 text-left transition-colors hover:bg-purple-50 dark:hover:bg-purple-900/20',
                      value === city && 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    )}
                  >
                    <div className='flex items-center space-x-3'>
                      <MapPin className='h-4 w-4 text-purple-500' />
                      <span className='font-medium'>{city}</span>
                    </div>
                    {value === city && <Check className='h-4 w-4' />}
                  </button>
                ))
              ) : (
                <div className='px-4 py-3 text-sm text-gray-500 dark:text-gray-400 italic'>
                  {t('noResults')}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

